import React, { useState, useEffect } from "react";
import {
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { SERVER_BASE_URL } from "../Config";

interface EmailType {
  key: string;
  label: string;
}

interface EmailPreferencesProps {
  tokenKey: string;
  emailTypes: EmailType[];
  defaultOn?: boolean; // true = checked when no preference exists (providers), false = unchecked (pharmacists)
}

const EmailPreferences: React.FC<EmailPreferencesProps> = ({ tokenKey, emailTypes, defaultOn = true }) => {
  const [prefs, setPrefs] = useState<Record<string, boolean | undefined>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem(tokenKey);
        const res = await fetch(`${SERVER_BASE_URL}/email-preferences`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const map: Record<string, boolean> = {};
          for (const p of data.preferences) {
            map[p.emailType] = p.unsubscribed;
          }
          setPrefs(map);
        }
      } catch (err) {
        console.error("Failed to load email preferences:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [tokenKey]);

  const handleToggle = async (emailType: string, unsubscribed: boolean) => {
    const previous = prefs[emailType];
    setPrefs((prev) => ({ ...prev, [emailType]: unsubscribed }));
    try {
      const token = localStorage.getItem(tokenKey);
      await fetch(`${SERVER_BASE_URL}/email-preferences`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ emailType, unsubscribed }),
      });
    } catch (err) {
      console.error("Failed to update preference:", err);
      setPrefs((prev) => ({ ...prev, [emailType]: previous }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Email Notifications
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose which email notifications you'd like to receive.
      </Typography>
      <FormGroup>
        {emailTypes.map((et) => {
          const isUnsubscribed = prefs[et.key];
          const isChecked = isUnsubscribed === undefined ? defaultOn : !isUnsubscribed;
          return (
            <FormControlLabel
              key={et.key}
              control={
                <Checkbox
                  checked={isChecked}
                  onChange={() => handleToggle(et.key, isChecked)}
                />
              }
              label={et.label}
              sx={{ mb: 1, "& .MuiFormControlLabel-label": { fontSize: "0.95rem" } }}
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};

export default EmailPreferences;
